import { z } from 'zod';

import { US_STATES } from '../constants/options';

// SSN format: XXX-XX-XXXX (3 digits, dash, 2 digits, dash, 4 digits)
const SSN_PATTERN = /^\d{3}-\d{2}-\d{4}$/;

// Phone format: X{1-3}-X{1-3}-X{1-4} (1-3 digits, dash, 1-3 digits, dash, 1-4 digits)
const PHONE_PATTERN = /^\d{1,3}-\d{1,3}-\d{1,4}$/;

// ZIP code format: 5 digits optionally followed by dash and 4 digits (ZIP+4)
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

// Valid two-letter state/territory codes — mirrors the US_STATES dropdown so
// the schema can never reject a value the dropdown offers. Case-insensitive
// so semantically-valid legacy free-text values (e.g. "fl") don't block saves;
// the backend normalizes state to uppercase on write (ROG-3025).
export const US_STATE_CODES = new Set<string>(US_STATES.map(s => s.value));

export interface CreateEpisodeStepFormSchemaOptions {
  /** When true (RogerDoc clinic has auto_mrn), MRN is not required for Roger or Kinnser intake. */
  autoMrn?: boolean;
  /**
   * REC (recert onboarding): the patient keeps their original MRN from the
   * prior EMR, so an explicit MRN is required (and format-checked) even when
   * the clinic has `autoMrn`. Overrides `autoMrn`.
   */
  requireExplicitMrn?: boolean;
}

/** Require typed manual MRN on `kinnser_mrn` when pushing to Roger EMR or Kinnser EMR unless the
 * clinic reports `autoMrn` (overridden by `requireExplicitMrn` for REC onboarding)
 * (Roger EMR uses the same `kinnser_mrn` field name as legacy Kinnser onboarding). */
export function addMrnIssueWhenRequired(
  data: {
    kinnser_mrn?: string | null;
    push_to_roger?: boolean | null;
    push_to_kinnser?: boolean | null;
  },
  ctx: z.RefinementCtx,
  options: CreateEpisodeStepFormSchemaOptions
): void {
  const requireExplicit = options.requireExplicitMrn === true;
  const mrnAutoAssigned = options.autoMrn === true && !requireExplicit;
  const mrn = data.kinnser_mrn;
  const mrnMissing = !mrn || mrn.trim().length === 0;
  const pushEmr = !!(data.push_to_roger || data.push_to_kinnser);
  const mrnRequired = mrnMissing && pushEmr && !mrnAutoAssigned;
  if (mrnRequired) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: requireExplicit ? 'Original MRN is required' : 'MRN is required',
      path: ['kinnser_mrn'],
    });
  }

  if (
    !mrnAutoAssigned &&
    mrn &&
    (!/^[A-Za-z0-9]+$/.test(mrn) || mrn.toLowerCase().startsWith('test'))
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'MRN must contain only letters and numbers and cannot start with "test"',
      path: ['kinnser_mrn'],
    });
  }
}

function createEpisodeStepFormSchemaInner(
  options: CreateEpisodeStepFormSchemaOptions = {}
) {
  return z
    .object({
      // Push to EMR flags
      push_to_roger: z.boolean().nullable().optional(),
      push_to_kinnser: z.boolean().nullable().optional(),

      // Push to EMR fields
      has_transferred_from_another_hha: z.boolean().nullable().optional(),
      prior_hha: z.string().nullable().optional(),
      f2f_encounter_date: z.string().nullable().optional(),

      // Clinic selection (stores agency_clinic_id UUID; clinic_key resolved at save time)
      agency_clinic_id: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Clinic is required'),
      kinnser_physician_id: z.string().nullable().optional(),
      kinnser_branch_id: z.string().nullable().optional(),
      kinnser_insurance_provider_id: z
        .string()
        .nullable()
        .optional()
        .refine(
          val => val && val.length >= 1,
          'Insurance Provider is required'
        ),
      // Both referral fields are optional: an empty value leaves WellSky's own
      // form default in place. The external value is the packed
      // "{referral_key}|{type_key}" string and is never split.
      kinnser_internal_referral_id: z.string().nullable().optional(),
      kinnser_external_referral_id: z.string().nullable().optional(),
      // The picked external referral's name; never rendered as a field.
      kinnser_external_referral_name: z.string().nullable().optional(),
      kinnser_admission_type: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Admission Type is required'),
      // Optional, unlike Admission Type: an empty value leaves WellSky's own
      // default in place, which is what happens for every patient today.
      kinnser_admission_source: z.string().nullable().optional(),
      kinnser_hcpcs_code_key: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'HCPCS Code is required'),
      kinnser_mrn: z.string().nullable().optional(),
      kinnser_hh_cahps_opt_out: z.boolean().nullable().optional(),
      kinnser_language_key: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Language is required'),
      kinnser_other_language: z.string().nullable().optional(),

      // Patient Identification (8 fields)
      patient_first_name: z
        .string()
        .max(19, 'First name must be 19 characters or less')
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'First name is required'),
      patient_middle_name: z.string().nullable().optional(),
      patient_last_name: z
        .string()
        .max(20, 'Last name must be 20 characters or less')
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Last name is required'),
      patient_suffix: z.string().nullable().optional(),
      patient_dob: z.string().nullable().optional(),
      patient_gender: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Gender is required'),
      patient_ssn: z
        .string()
        .refine(
          val => !val || SSN_PATTERN.test(val),
          'SSN must be in format XXX-XX-XXXX (e.g., 123-45-6789)'
        )
        .nullable()
        .optional(),
      patient_primary_language: z.string().nullable().optional(),

      // Contact Information (8 fields)
      patient_phone: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Phone is required')
        .refine(
          val => !val || PHONE_PATTERN.test(val),
          'Phone must be in format aaa-bbb-cccc (e.g., 555-123-4567)'
        ),
      patient_secondary_phone: z
        .string()
        .nullable()
        .optional()
        .refine(
          val => !val || PHONE_PATTERN.test(val),
          'Secondary phone must be in format aaa-bbb-cccc (e.g., 555-123-4567)'
        ),
      patient_email: z
        .string()
        .refine(
          val => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
          'Invalid email format'
        )
        .nullable()
        .optional(),
      patient_street_address: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Address is required'),
      patient_city: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'City is required'),
      patient_state: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'State is required')
        .refine(
          val => !val || US_STATE_CODES.has(val.trim().toUpperCase()),
          'Select a valid state'
        ),
      patient_zip_code: z
        .string()
        .nullable()
        .optional()
        .refine(val => val && val.length >= 1, 'Zip Code is required')
        .refine(
          val => !val || ZIP_PATTERN.test(val),
          'Zip Code must be in format 12345 or 12345-6789'
        ),
      patient_county: z.string().nullable().optional(),

      // Emergency contact (optional; pushed to WellSky when create/onboard)
      emergency_contact_name: z.string().nullable().optional(),
      emergency_contact_relationship: z.string().nullable().optional(),
      emergency_contact_phone: z
        .string()
        .nullable()
        .optional()
        .refine(
          val => !val || PHONE_PATTERN.test(val),
          'Emergency contact phone must be in format aaa-bbb-cccc (e.g., 555-123-4567)'
        ),
      emergency_contact_street_address: z.string().nullable().optional(),
      emergency_contact_city: z.string().nullable().optional(),
      emergency_contact_state: z.string().nullable().optional(),
      emergency_contact_zip_code: z
        .string()
        .nullable()
        .optional()
        .refine(
          val => !val || ZIP_PATTERN.test(val),
          'Emergency contact ZIP must be in format 12345 or 12345-6789'
        ),

      // Insurance Information
      medicaid_id: z.string().nullable().optional(),

      // ClinicPhysician ID (episode.clinic_physician_id / intake.clinic_physician_id)
      clinic_physician_id: z.string().nullable().optional(),

      // Internal referral source (intake.internal_referral_source_id). RogerEMR
      // push only, and optional: an agency that has not populated its list yet
      // must still be able to push, and not every referral has one.
      internal_referral_source_id: z.string().nullable().optional(),

      // Notes (1 field)
      patient_notes: z.string().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      // Kinnser-only: physician and branch are required
      if (data.push_to_kinnser) {
        if (
          !data.kinnser_physician_id ||
          data.kinnser_physician_id.length < 1
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Physician is required',
            path: ['kinnser_physician_id'],
          });
        }
        if (!data.kinnser_branch_id || data.kinnser_branch_id.length < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Branch is required',
            path: ['kinnser_branch_id'],
          });
        }
      }

      // Roger-only: clinic_physician_id is required
      if (data.push_to_roger) {
        if (
          !data.clinic_physician_id ||
          data.clinic_physician_id.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Physician is required',
            path: ['clinic_physician_id'],
          });
        }
      }

      // Other Language is required when language key is '6' (Other/Unknown)
      if (
        data.kinnser_language_key === '6' &&
        (!data.kinnser_other_language ||
          data.kinnser_other_language.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Other Language is required when "Other/Unknown" is selected',
          path: ['kinnser_other_language'],
        });
      }

      addMrnIssueWhenRequired(data, ctx, options);
    });
}

/** Default schema without clinic-loaded autoMrn (unknown → manual MRN required when pushing to an EMR). Use createEpisodeStepFormSchemaWithOptions in the episode-step hook. */
export const createEpisodeStepFormSchema = createEpisodeStepFormSchemaInner();

/** Build schema with clinic-aware autoMrn (same rule for Roger and Kinnser push). */
export function createEpisodeStepFormSchemaWithOptions(
  options: CreateEpisodeStepFormSchemaOptions
) {
  return createEpisodeStepFormSchemaInner(options);
}

export type CreateEpisodeStepFormData = z.infer<
  typeof createEpisodeStepFormSchema
>;
